import type {CodeItem} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';
import {
  resolveApprovedWritingGuideRelease,
  type ApprovedWritingGuideRelease,
} from '../../vendor/src/features/remotion-editor/editor/items/code/approved-writing-guide-release';
import {WRITING_GUIDE_RENDER_HOST_CONTRACT} from '../../vendor/src/features/remotion-editor/editor/items/code/writing-guide-render-host-contract';
import {AUTHORING_WRITING_GUIDE_TEMPLATE_ID} from '../../vendor/src/features/remotion-editor/editor/items/code/code-item-type';
import {assertPinnedWritingGuideRelease} from './pinned-writing-guide-bundles';

const MAX_SPEC_BYTES = 8 * 1024 * 1024;
const MAX_WORD_LENGTH = 32;
const MAX_DURATION_FRAMES = 15 * 60 * 60;

function invalid(): never {
  throw Error('승인된 따라쓰기 구조화 데이터가 올바르지 않습니다. 사본에서 효과를 다시 생성해 주세요.');
}

/** Check values that the Canvas renderer may walk before cloning or executing anything. */
function assertFiniteTree(value: unknown): void {
  const seen = new Set<object>();
  let nodes = 0;
  const walk = (current: unknown, depth: number) => {
    if (++nodes > 200_000 || depth > 20) invalid();
    if (current === null || typeof current === 'boolean') return;
    if (typeof current === 'number') {
      if (!Number.isFinite(current) || Math.abs(current) > 1_000_000_000) invalid();
      return;
    }
    if (typeof current === 'string') {
      if (current.length > 16_384) invalid();
      return;
    }
    if (typeof current !== 'object' || seen.has(current)) invalid();
    seen.add(current);
    if (Array.isArray(current)) {
      if (current.length > 2_048) invalid();
      current.forEach(entry => walk(entry, depth + 1));
    } else {
      const keys = Object.keys(current);
      if (keys.length > 256) invalid();
      for (const key of keys) {
        if (key.length > 256 || key === '__proto__' || key === 'constructor') invalid();
        walk((current as Record<string, unknown>)[key], depth + 1);
      }
    }
    seen.delete(current);
  };
  walk(value, 0);
}

export type TrustedWritingGuideSpec = {
  releases: ApprovedWritingGuideRelease[];
  runtime: Record<string, unknown>;
  word: string;
  layoutPolicy: Record<string, unknown>;
  layoutOverride: unknown;
  strokeTimeline: unknown[];
  timelineOptions: Record<string, unknown>;
  style: Record<string, unknown>;
  durationSec: number;
  fps: number;
};

/** Browser export accepts the legacy host catalog and four pinned company bundles. */
export function readTrustedWritingGuideSpec(item: CodeItem): TrustedWritingGuideSpec {
  if (item.templateId !== AUTHORING_WRITING_GUIDE_TEMPLATE_ID ||
      item.authoringSpec?.version !== 'alphabet.authoring_render_spec.v2') invalid();
  const authoring = item.authoringSpec;
  const authoredDurationSec = item.durationSec ?? 5;
  const authoredFps = item.fps ?? 30;
  if (!Number.isFinite(authoredDurationSec) || authoredDurationSec <= 0 ||
      !Number.isFinite(authoredFps) || authoredFps <= 0 || authoredFps > 120) invalid();
  // Match AuthoringWritingGuideCodeLayer's historical playback clock without
  // changing the saved JSON. Bound work after the same normalization.
  const durationSec = Math.max(1, authoredDurationSec);
  const fps = Math.max(1, authoredFps);
  if (Math.ceil(durationSec * fps) > MAX_DURATION_FRAMES) invalid();
  const word = authoring.word.trim();
  if (!word || word.length > MAX_WORD_LENGTH || !/^[a-zA-Z]+$/.test(word)) invalid();
  const value = {
    runtime: authoring.runtime,
    layoutPolicy: authoring.layoutPolicy,
    layoutOverride: authoring.layoutOverride ?? null,
    strokeTimeline: authoring.strokeTimeline ?? [],
    timelineOptions: authoring.timelineOptions ?? {},
    style: {...(authoring.style ?? {}), ...(item.authoringStyle ?? {})},
  };
  assertFiniteTree(value);
  let serialized: string;
  try { serialized = JSON.stringify(value); } catch { invalid(); }
  if (new TextEncoder().encode(serialized).byteLength > MAX_SPEC_BYTES) invalid();
  const resolved = resolveApprovedWritingGuideRelease(item, {...authoring, word});
  if (resolved.releases.length < 1 || resolved.releases.length > 2) invalid();
  const bundled = resolved.releases[0]!.runtimeVersion === 'alphabet.authoring_runtime.v2';
  if (bundled) {
    const manifests = authoring.runtime.bundleManifests;
    if (!Array.isArray(manifests) || manifests.length !== resolved.releases.length) invalid();
    for (const release of resolved.releases) {
      const claimed = manifests.find(manifest =>
        manifest && typeof manifest === 'object' && !Array.isArray(manifest) &&
        (manifest as Record<string, unknown>).alphabetCase === release.alphabetCase);
      assertPinnedWritingGuideRelease(release, claimed);
    }
  } else if (resolved.releases.some(release =>
    release.runtimeVersion !== 'alphabet.authoring_runtime.v1' ||
    release.bundleContentHash ||
    !WRITING_GUIDE_RENDER_HOST_CONTRACT.approvedReleases.some(candidate =>
      candidate.releaseId === release.releaseId &&
      candidate.runtimeAssetPath === release.runtimeAssetPath &&
      candidate.runtimeAssetSourceHash === release.runtimeAssetSourceHash &&
      candidate.rendererAssetPath === release.rendererAssetPath &&
      candidate.rendererSourceHash === release.rendererSourceHash))) invalid();
  const renderer = resolved.releases[0]!;
  if (resolved.releases.some(release => release.rendererSourceHash !== renderer.rendererSourceHash ||
      (!bundled && release.rendererAssetPath !== renderer.rendererAssetPath) ||
      release.rendererVersion !== renderer.rendererVersion)) invalid();
  const matchesIfPresent = (value:unknown, expected:unknown) => value == null || value === expected;
  if (!matchesIfPresent(item.rendererSourceHash, renderer.rendererSourceHash) ||
      (bundled
        ? item.rendererAssetPath != null && !resolved.releases.some(release => release.rendererAssetPath === item.rendererAssetPath)
        : !matchesIfPresent(item.rendererAssetPath, renderer.rendererAssetPath)) ||
      !matchesIfPresent(item.rendererVersion, renderer.rendererVersion) ||
      !matchesIfPresent(item.runtimeSourceHash, authoring.runtime.sourceStateHash)) invalid();
  return {
    releases: resolved.releases,
    runtime: resolved.runtime,
    word,
    layoutPolicy: authoring.layoutPolicy as unknown as Record<string, unknown>,
    layoutOverride: value.layoutOverride,
    strokeTimeline: value.strokeTimeline,
    timelineOptions: value.timelineOptions,
    style: value.style,
    durationSec,
    fps,
  };
}
